import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  X,
  ExternalLink,
  CalendarDays,
  Loader2,
  ShieldCheck,
  Plus,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  ShoppingBag,
  Check,
  MapPin,
  Search,
  MessageCircle,
  ListPlus,
} from "lucide-react";
import {
  Button,
  Card,
  Badge,
  Dialog,
  DialogBody,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogDescription,
  DialogFooter,
  cn,
  toast,
  Input,
  Label,
  ImageWithSkeleton,
  SessionTypeServiceSelect,
  serviceItemImageObjectPositionClass,
} from "@cliently/ui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { resolveOrganizationMediaUrl } from "../lib/organization-media-url";
import { formatEuroNumber, formatMoneyEur } from "../lib/format-money";
import { createPortalConversation } from "../lib/chatApi";
import { useClientProfile } from "../hooks/use-client-profile";
import {
  useAvailableSlots,
  useClosedDates,
  useJoinWaitlist,
  type AvailableSlotDto,
} from "../hooks/use-availability";
import { IntakeFormFields } from "../components/IntakeFormFields";
import { InfoCallout } from "../components/InfoCallout";
import { useOrganizations } from "../hooks/use-organizations";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { LottieIllustration } from "../components/LottieIllustration";
import emptyBoxLottie from "../assets/lottie/empty-box.json";
import { EmptyIllustration } from "../assets/undraw/empty";
import type {
  AppointmentStatus,
  IntakeField,
  PendingConfirmationBy,
} from "@cliently/types";
import type { PortalServiceItem } from "../types/portal-service-item";
import {
  isSessionPackExhausted,
  richTextToPlain,
  shouldShowSessionPackRemaining,
} from "@cliently/schemas";

const statusColors: Record<AppointmentStatus, string> = {
  PENDING: "bg-yellow-500",
  CONFIRMED: "bg-blue-500",
  DONE: "bg-green-500",
  CANCELLED: "bg-red-500",
};



const useClientAppointments = (activeClientId: string | null) => {
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  return useQuery({
    queryKey: ["client-appointments", activeClientId],
    queryFn: async () => {
      const { data } = await api.get(
        isAllProsMode
          ? "/portal/aggregate/appointments"
          : "/portal/appointments",
      );
      return data as Array<{
        id: string;
        startsAt: string;
        duration: number;
        serviceItemId: string | null;
        price: number;
        status: AppointmentStatus;
        pendingConfirmationBy?: PendingConfirmationBy;
        type: string | null;
        notes: string | null;
        confirmationMessage: string | null;
        depositAmount: number;
        depositPaid: boolean;
        confirmedAt?: string | null;
        locationId?: string | null;
        location?: { id: string; name: string; address?: string | null } | null;
        org?: { id: string; name: string };
      }>;
    },
  });
}



const useCancelMyAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/portal/appointments/${id}/cancel`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["client-appointments"] });
      void qc.invalidateQueries({ queryKey: ["portal-service-items"] });
      void qc.invalidateQueries({ queryKey: ["portal-service-items-aggregate"] });
    },
  });
}



const useRescheduleAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, startsAt }: { id: string; startsAt: string }) => {
      await api.patch(`/portal/appointments/${id}/reschedule`, { startsAt });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["client-appointments"] }),
  });
}



const useBookAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      startsAt: string;
      duration: number;
      type?: string;
      serviceItemId?: string;
      locationId?: string;
    }) => {
      const { data } = await api.post("/portal/book", dto);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["client-appointments"] });
      void qc.invalidateQueries({ queryKey: ["portal-service-items"] });
      void qc.invalidateQueries({ queryKey: ["portal-service-items-aggregate"] });
    },
  });
}



const useAggregateBookAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: {
      orgId: string;
      startsAt: string;
      duration: number;
      type?: string;
      serviceItemId?: string;
      locationId?: string;
    }) => {
      const { data } = await api.post("/portal/aggregate/book", dto);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["client-appointments"] });
      void qc.invalidateQueries({ queryKey: ["portal-service-items"] });
      void qc.invalidateQueries({ queryKey: ["portal-service-items-aggregate"] });
    },
  });
}



const useConfirmMyAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/portal/appointments/${id}/confirm`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["client-appointments"] }),
  });
}



const useAvailableDays = () => {
  return useQuery({
    queryKey: ["available-days"],
    queryFn: async () => {
      const { data } = await api.get("/portal/availability/days");
      return data as Array<{
        dayOfWeek: number;
        startTime: string;
        endTime: string;
      }>;
    },
    staleTime: 1000 * 60 * 10,
  });
}



const useIntakeStatus = () => {
  return useQuery({
    queryKey: ["portal-intake"],
    queryFn: async () => {
      const { data } = await api.get("/portal/intake");
      return data as {
        intakeForm: IntakeField[] | null;
        intakeFormVersion: number;
        clientData: Record<string, unknown> | null;
        clientVersion: number;
        needsUpdate: boolean;
      };
    },
  });
}



const useSubmitIntake = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (intakeData: Record<string, unknown>) => {
      const { data } = await api.post("/portal/intake", { intakeData });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-intake"] }),
  });
}



const useSubmitIntakeWithDocuments = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      intakeData,
      intakeFields,
      files,
    }: {
      intakeData: Record<string, unknown>;
      intakeFields: IntakeField[];
      files: Record<string, File | null>;
    }) => {
      const form = new FormData();
      form.append("intakeData", JSON.stringify(intakeData));

      for (const field of intakeFields) {
        if (field.type !== "document") continue;
        const file = files[field.id];
        if (file) form.append(field.id, file);
      }

      const { data } = await api.post("/portal/intake-with-documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-intake"] }),
  });
}



const usePortalServiceItems = () => {
  return useQuery({
    queryKey: ["portal-service-items"],
    queryFn: async () => {
      const { data } = await api.get("/portal/service-items");
      return data as PortalServiceItem[];
    },
    staleTime: 1000 * 60 * 10,
  });
}



const useAggregateServiceItems = (orgId: string | null) => {
  return useQuery({
    queryKey: ["portal-service-items-aggregate", orgId],
    queryFn: async () => {
      const { data } = await api.get("/portal/aggregate/service-items", {
        params: { orgId },
      });
      return data as PortalServiceItem[];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60 * 10,
  });
}



const useAggregateAvailableDays = (orgId: string | null) => {
  return useQuery({
    queryKey: ["available-days-aggregate", orgId],
    queryFn: async () => {
      const { data } = await api.get("/portal/aggregate/availability/days", {
        params: { orgId },
      });
      return data as Array<{
        dayOfWeek: number;
        startTime: string;
        endTime: string;
      }>;
    },
    enabled: !!orgId,
    staleTime: 1000 * 60 * 10,
  });
}



const useAggregateClosedDates = (orgId: string | null, from: string | null, to: string | null) => {
  return useQuery({
    queryKey: ["closed-dates-aggregate", orgId, from, to],
    queryFn: async () => {
      const { data } = await api.get("/portal/aggregate/availability/closed-dates", {
        params: { orgId, from, to },
      });
      return data as string[];
    },
    enabled: !!orgId && !!from && !!to,
    staleTime: 1000 * 60 * 5,
  });
}



const useAggregateAvailableSlots = (orgId: string | null, date: string | null, duration: number, serviceItemId?: string | null) => {
  return useQuery({
    queryKey: [
      "availability-slots-aggregate",
      orgId,
      date,
      duration,
      serviceItemId ?? undefined,
    ],
    queryFn: async () => {
      const { data } = await api.get("/portal/aggregate/availability/slots", {
        params: {
          orgId,
          date,
          duration,
          ...(serviceItemId ? { serviceItemId } : {}),
        },
      });
      return data as AvailableSlotDto[];
    },
    enabled: !!orgId && !!date,
  });
}

const BASE_DURATION_OPTIONS = [60, 45, 30, 90, 120];



const normalizeDurationMinutes = (m: number) => {
  return Math.min(480, Math.max(15, Math.round(m / 15) * 15));
}
const INTAKE_PAGE_SIZE = 5;



const MiniCalendar = ({
  activeDays,
  closedDates = [],
  selectedDate,
  onSelect,
  onMonthChange,
}: {
  activeDays: number[];
  closedDates?: string[];
  selectedDate: string;
  onSelect: (date: string) => void;
  onMonthChange?: (from: string, to: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const closedSet = new Set(closedDates);

  const changeMonth = (newMonth: Date) => {
    setViewMonth(newMonth);
    const y = newMonth.getFullYear();
    const m = newMonth.getMonth();
    onMonthChange?.(
      `${y}-${String(m + 1).padStart(2, "0")}-01`,
      `${y}-${String(m + 1).padStart(2, "0")}-${new Date(y, m + 1, 0).getDate()}`,
    );
  };

  const prevMonth = () => changeMonth(new Date(year, month - 1, 1));
  const nextMonth = () => changeMonth(new Date(year, month + 1, 1));

  const cells: (Date | null)[] = [];
  const startDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = viewMonth.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });

  const dayHeaders = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, i + 1);
    return d.toLocaleDateString(locale, { weekday: "short" });
  });

  const dayNames = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, i);
    return d.toLocaleDateString(locale, { weekday: "short" });
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={prevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium capitalize">{monthLabel}</span>
        <Button variant="ghost" size="icon" onClick={nextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {dayHeaders.map((d) => (
          <div
            key={d}
            className="text-muted-foreground py-1 text-[10px] font-medium"
          >
            {d}
          </div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={`empty-${i}`} />;

          const isPast = date < today;
          const dayOfWeek = date.getDay();
          const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
          const isClosed = closedSet.has(dateStr);
          const isAvailable =
            !isPast && !isClosed && activeDays.includes(dayOfWeek);
          const isSelected = dateStr === selectedDate;
          const isToday = date.getTime() === today.getTime();

          return (
            <Button
              key={dateStr}
              type="button"
              variant="ghost"
              size="icon"
              disabled={!isAvailable}
              onClick={() => onSelect(dateStr)}
              className={cn(
                "mx-auto h-8 w-8 rounded-full p-0 text-xs font-normal shadow-none transition-colors disabled:opacity-100",
                isSelected
                  ? "bg-primary text-primary-foreground font-bold hover:bg-primary hover:text-primary-foreground"
                  : isClosed && !isPast
                    ? "cursor-not-allowed bg-red-100 text-red-400 line-through hover:bg-red-100 dark:bg-red-950/30 dark:text-red-500/60"
                    : isAvailable
                      ? "hover:bg-primary/10 text-foreground font-medium"
                      : "text-muted-foreground/40 cursor-not-allowed hover:bg-transparent",
                isToday && !isSelected && "ring-primary ring-1",
              )}
            >
              {date.getDate()}
            </Button>
          );
        })}
      </div>
      {activeDays.length > 0 && (
        <div className="text-muted-foreground flex items-center gap-2 text-[10px]">
          <div className="bg-primary h-2 w-2 rounded-full" />
          <span>
            {t("booking.available")} :{" "}
            {activeDays
              .sort((a, b) => a - b)
              .map((d) => dayNames[d])
              .join(", ")}
          </span>
        </div>
      )}
    </div>
  );
}



const isAppointmentModificationBlocked = (appt: { startsAt: string }, minLeadMinutes: number | null | undefined): boolean => {
  const m = typeof minLeadMinutes === "number" ? minLeadMinutes : 0;
  if (m <= 0) return false;
  return new Date(appt.startsAt).getTime() - Date.now() < m * 60 * 1000;
}



const generateGoogleCalendarUrl = (appt: {
  startsAt: string;
  duration: number;
  type: string | null;
}) => {
  const start = new Date(appt.startsAt);
  const end = new Date(start.getTime() + appt.duration * 60 * 1000);
  const fmt = (d: Date) =>
    d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const title = encodeURIComponent(appt.type || "Rendez-vous Cliently");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}`;
}



const BookingWizardSelectedServiceRecap = ({
  service,
  locale,
  stepLabel,
}: {
  service: PortalServiceItem;
  locale: string;
  stepLabel: string;
}) => {
  const svcImage = service.image
    ? (resolveOrganizationMediaUrl(service.image) ?? service.image)
    : null;
  const descPreview = richTextToPlain(service.description)
    .replace(/\s+/g, " ")
    .trim();

  return (
    <div className="bg-muted/50 flex gap-3 rounded-lg border px-3 py-2.5">
      <div className="from-primary/20 via-primary/10 to-background relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-linear-to-br">
        {svcImage ? (
          <ImageWithSkeleton
            src={svcImage}
            alt={service.label}
            className="absolute inset-0 h-full w-full"
            imageClassName={cn(
              "h-full w-full object-cover",
              serviceItemImageObjectPositionClass(service.imageObjectPosition),
            )}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ShoppingBag className="text-primary/50 h-7 w-7 shrink-0" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
          {stepLabel}
        </p>
        <p className="text-sm font-semibold leading-snug">{service.label}</p>
        <p className="text-muted-foreground text-xs">
          {service.duration} min ·{" "}
          {formatMoneyEur(service.price, locale, {
            forceDecimals: true,
          })}
        </p>
        {descPreview ? (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-[11px] leading-snug">
            {descPreview}
          </p>
        ) : null}
      </div>
    </div>
  );
};



export const AppointmentsPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusLabels: Record<AppointmentStatus, string> = {
    PENDING: t("status.pending"),
    CONFIRMED: t("status.confirmed"),
    DONE: t("status.done"),
    CANCELLED: t("status.cancelled"),
  };
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: portalProfile } = useClientProfile();
  const orgCancellation = portalProfile?.org as
    | {
      clientCancellationMinLeadMinutes?: number;
      clientCancellationPolicyText?: string | null;
    }
    | undefined;
  const cancellationMinLead = !isAllProsMode
    ? (orgCancellation?.clientCancellationMinLeadMinutes ?? 0)
    : 0;
  const cancellationPolicyText = !isAllProsMode
    ? orgCancellation?.clientCancellationPolicyText
    : null;

  const { data: appointments, isLoading } = useClientAppointments(activeClientId);

  useEffect(() => {
    const aid = searchParams.get("appointmentId");
    if (!aid || isLoading) return;
    setTab("upcoming");
    const timer = window.setTimeout(() => {
      document
        .getElementById(`appointment-card-${aid}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [searchParams, isLoading, appointments]);

  const [chatCreatingApptId, setChatCreatingApptId] = useState<string | null>(
    null,
  );
  const cancelMutation = useCancelMyAppointment();
  const rescheduleMutation = useRescheduleAppointment();
  const bookMutation = useBookAppointment();
  const aggregateBookMutation = useAggregateBookAppointment();
  const confirmMyMutation = useConfirmMyAppointment();
  const joinWaitlistMutation = useJoinWaitlist();
  const { data: organizations = [] } = useOrganizations();
  const [selectedOrgIdForBooking, setSelectedOrgIdForBooking] = useState<
    string | null
  >(null);
  const [tab, setTab] = useState<"upcoming" | "past">("upcoming");
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<string>("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [pendingCancelAppt, setPendingCancelAppt] = useState<{
    id: string;
    depositPaid: boolean;
    depositAmount: number;
  } | null>(null);
  const rescheduleAppt = (appointments || []).find(
    (a) => a.id === rescheduleId,
  );

  const openCancelDialog = (appt: {
    id: string;
    depositPaid: boolean;
    depositAmount: number;
  }) => {
    setPendingCancelAppt({
      id: appt.id,
      depositPaid: appt.depositPaid,
      depositAmount: appt.depositAmount,
    });
    setCancelDialogOpen(true);
  };

  const confirmCancelAppointment = () => {
    if (!pendingCancelAppt) return;
    cancelMutation.mutate(pendingCancelAppt.id, {
      onSuccess: () => {
        toast.success(t("appointments.cancelSuccess"));
        setCancelDialogOpen(false);
        setPendingCancelAppt(null);
      },
      onError: () => toast.error(t("appointments.cancelError")),
    });
  };

  const cancelDialogDeposit =
    pendingCancelAppt &&
    pendingCancelAppt.depositPaid &&
    pendingCancelAppt.depositAmount > 0;
  const cancelDialogDescription = pendingCancelAppt
    ? cancelDialogDeposit
      ? t("cancel.confirmWithDeposit", {
        amount: formatEuroNumber(pendingCancelAppt.depositAmount, locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      })
      : t("cancel.dialogBody")
    : "";

  const [showBooking, setShowBooking] = useState<boolean>(false);
  const [bookingDate, setBookingDate] = useState<string>("");
  const [bookingDuration, setBookingDuration] = useState<number>(60);
  const [bookingType, setBookingType] = useState<string>("");
  const [selectedServiceItem, setSelectedServiceItem] =
    useState<PortalServiceItem | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [serviceSearch, setServiceSearch] = useState<string>("");
  const [bookingWizardStep, setBookingWizardStep] = useState<1 | 2 | 3>(1);

  const effectiveOrgId = isAllProsMode
    ? selectedOrgIdForBooking
    : organizations.find((o) => o.clientId === activeClientId)?.orgId ?? null;

  const clientBookingDefaultDuration = useMemo(() => {
    if (isAllProsMode) {
      const o = organizations.find((x) => x.orgId === selectedOrgIdForBooking);
      return o?.defaultSessionDuration ?? 60;
    }
    const o = organizations.find((x) => x.clientId === activeClientId);
    return o?.defaultSessionDuration ?? 60;
  }, [
    isAllProsMode,
    organizations,
    selectedOrgIdForBooking,
    activeClientId,
  ]);

  const durationOptions = useMemo(() => {
    const d = normalizeDurationMinutes(clientBookingDefaultDuration);
    const set = new Set(BASE_DURATION_OPTIONS);
    set.add(d);
    return Array.from(set).sort((a, b) => a - b);
  }, [clientBookingDefaultDuration]);

  const { data: portalServiceItems, isPending: portalServiceItemsPending } =
    usePortalServiceItems();
  const { data: aggregateServiceItems, isPending: aggregateServiceItemsPending } =
    useAggregateServiceItems(effectiveOrgId);
  const currentServiceItems = useMemo((): PortalServiceItem[] => {
    return isAllProsMode
      ? (aggregateServiceItems ?? [])
      : (portalServiceItems ?? []);
  }, [isAllProsMode, aggregateServiceItems, portalServiceItems]);
  const hasServiceItems = currentServiceItems.length > 0;
  const serviceCatalogPending = isAllProsMode
    ? !effectiveOrgId || aggregateServiceItemsPending
    : portalServiceItemsPending;
  const bookingUsesServiceCatalog =
    serviceCatalogPending || hasServiceItems;
  const bookingNeedsServicePick =
    bookingUsesServiceCatalog && !selectedServiceItem;

  const { data: intakeStatus } = useIntakeStatus();
  const submitIntakeMutation = useSubmitIntake();
  const submitIntakeWithDocumentsMutation = useSubmitIntakeWithDocuments();
  const [showIntakeForm, setShowIntakeForm] = useState<boolean>(false);
  const [intakeValues, setIntakeValues] = useState<Record<string, string>>({});
  const [intakeFiles, setIntakeFiles] = useState<Record<string, File | null>>(
    {},
  );
  const [intakeErrors, setIntakeErrors] = useState<Record<string, string>>({});
  const [intakePage, setIntakePage] = useState<number>(0);

  const intakeFields = (intakeStatus?.intakeForm ?? []) as IntakeField[];
  const hasDocumentIntakeFields = intakeFields.some(
    (f) => f.type === "document",
  );
  const submitIntakePending = hasDocumentIntakeFields
    ? submitIntakeWithDocumentsMutation.isPending
    : submitIntakeMutation.isPending;
  const needsIntake = intakeStatus?.needsUpdate === true;
  const totalIntakePages = Math.max(
    1,
    Math.ceil(intakeFields.length / INTAKE_PAGE_SIZE),
  );
  const isLastIntakePage = intakePage >= totalIntakePages - 1;
  const visibleIntakeFields = intakeFields.slice(
    intakePage * INTAKE_PAGE_SIZE,
    (intakePage + 1) * INTAKE_PAGE_SIZE,
  );

  const { data: availableDaysData } = useAvailableDays();
  const { data: aggregateAvailableDaysData } =
    useAggregateAvailableDays(effectiveOrgId);
  const activeDays = (availableDaysData || []).map((d) => d.dayOfWeek);
  const bookingActiveDays = (
    isAllProsMode ? aggregateAvailableDaysData || [] : availableDaysData || []
  ).map((d) => d.dayOfWeek);

  const [calendarRange, setCalendarRange] = useState<{
    from: string;
    to: string;
  }>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return {
      from: `${y}-${String(m + 1).padStart(2, "0")}-01`,
      to: `${y}-${String(m + 1).padStart(2, "0")}-${new Date(y, m + 1, 0).getDate()}`,
    };
  });
  const { data: closedDates } = useClosedDates(
    calendarRange.from,
    calendarRange.to,
  );
  const { data: aggregateClosedDates } = useAggregateClosedDates(
    effectiveOrgId,
    calendarRange.from,
    calendarRange.to,
  );
  const bookingClosedDates = isAllProsMode
    ? (aggregateClosedDates ?? [])
    : (closedDates ?? []);

  const handleMonthChange = (from: string, to: string) => {
    setCalendarRange({ from, to });
  };

  const effectiveBookingDuration =
    selectedServiceItem?.duration ?? bookingDuration;

  const bookingSlotsDate =
    showBooking && bookingDate && !bookingNeedsServicePick ? bookingDate : null;

  const { data: bookingSlots, isLoading: loadingBookingSlots } =
    useAvailableSlots(
      bookingSlotsDate,
      effectiveBookingDuration,
      selectedServiceItem?.id,
    );
  const { data: aggregateBookingSlots, isLoading: loadingAggregateBookingSlots } =
    useAggregateAvailableSlots(
      showBooking ? effectiveOrgId : null,
      bookingSlotsDate,
      effectiveBookingDuration,
      selectedServiceItem?.id,
    );
  const effectiveBookingSlots = isAllProsMode
    ? aggregateBookingSlots
    : bookingSlots;
  const effectiveLoadingBookingSlots = isAllProsMode
    ? loadingAggregateBookingSlots
    : loadingBookingSlots;

  useEffect(() => {
    if (!showBooking || hasServiceItems || serviceCatalogPending) return;
    setBookingDuration(normalizeDurationMinutes(clientBookingDefaultDuration));
  }, [
    showBooking,
    hasServiceItems,
    serviceCatalogPending,
    clientBookingDefaultDuration,
  ]);

  useEffect(() => {
    if (!showBooking || serviceCatalogPending) return;
    if (hasServiceItems && !selectedServiceItem && bookingDate) {
      setBookingDate("");
      setBookingWizardStep((step) => (step > 1 ? 1 : step));
    }
  }, [
    showBooking,
    serviceCatalogPending,
    hasServiceItems,
    selectedServiceItem,
    bookingDate,
  ]);

  useEffect(() => {
    const locs = selectedServiceItem?.locations ?? [];
    const first = locs[0];
    if (locs.length === 1 && first && !selectedLocationId) {
      setSelectedLocationId(first.location.id);
    }
  }, [selectedServiceItem, selectedLocationId]);

  const bookingNeedsLocationPick = useMemo(() => {
    const locs = selectedServiceItem?.locations ?? [];
    return locs.length > 1 && !selectedLocationId;
  }, [selectedServiceItem, selectedLocationId]);

  /** Mobile (< lg) : après le choix du jour, passer à l'étape dédiée aux créneaux. */
  const handleSelectBookingDate = useCallback(
    (date: string) => {
      if (bookingNeedsServicePick) return;
      setBookingDate(date);
      if (
        bookingUsesServiceCatalog &&
        globalThis.matchMedia("(max-width: 1023px)").matches
      ) {
        setBookingWizardStep(3);
      }
    },
    [bookingNeedsServicePick, bookingUsesServiceCatalog],
  );

  const handleBookingWizardBack = useCallback(() => {
    setBookingWizardStep((s) => (s === 3 ? 2 : 1));
  }, []);

  const { data: availableSlots, isLoading: loadingSlots } = useAvailableSlots(
    rescheduleDate || null,
    rescheduleAppt?.duration || 60,
    rescheduleAppt?.serviceItemId ?? undefined,
  );

  const now = new Date();
  const upcoming = (appointments || [])
    .filter((a) => new Date(a.startsAt) >= now && a.status !== "CANCELLED")
    .sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    );
  const past = (appointments || [])
    .filter((a) => new Date(a.startsAt) < now || a.status === "CANCELLED")
    .sort(
      (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
    );

  const list = tab === "upcoming" ? upcoming : past;

  useEffect(() => {
    const from = searchParams.get("from");
    const orgIdParam = searchParams.get("orgId");
    const serviceItemIdParam = searchParams.get("serviceItemId");
    if (from !== "about" && !serviceItemIdParam) return;

    if (isAllProsMode && orgIdParam) {
      setSelectedOrgIdForBooking(orgIdParam);
    }
    if (isAllProsMode && orgIdParam && effectiveOrgId !== orgIdParam) {
      return;
    }

    const items = currentServiceItems;
    if (serviceItemIdParam && serviceCatalogPending) {
      return;
    }

    if (serviceItemIdParam && items.length > 0) {
      const found = items.find((i) => i.id === serviceItemIdParam);
      if (found) {
        setSelectedServiceItem(found);
        const locCount = found.locations?.length ?? 0;
        setBookingWizardStep(locCount > 1 ? 1 : 2);
      } else {
        setSelectedServiceItem(null);
        setBookingWizardStep(1);
      }
    } else {
      setSelectedServiceItem(null);
      setBookingWizardStep(1);
    }

    setShowBooking(true);
    setBookingDate("");
    setSearchParams({}, { replace: true });
  }, [
    searchParams,
    isAllProsMode,
    effectiveOrgId,
    currentServiceItems,
    serviceCatalogPending,
    setSearchParams,
  ]);

  const appointmentIdFromUrl = searchParams.get("appointmentId");

  useEffect(() => {
    if (!appointmentIdFromUrl || !appointments?.length) return;
    const appt = appointments.find((a) => a.id === appointmentIdFromUrl);
    if (!appt) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete("appointmentId");
        return p;
      }, { replace: true });
      return;
    }
    const now = new Date();
    const isPast =
      new Date(appt.startsAt) < now || appt.status === "CANCELLED";
    const targetTab = isPast ? "past" : "upcoming";
    if (tab !== targetTab) {
      setTab(targetTab);
      return;
    }
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document
          .getElementById(`appointment-card-${appointmentIdFromUrl}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.delete("appointmentId");
          return p;
        }, { replace: true });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [
    appointmentIdFromUrl,
    appointments,
    tab,
    setSearchParams,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("appointments.title")}
          </h1>
          <p className="text-muted-foreground">
            {upcoming.length} {t("appointments.upcoming")}
          </p>
        </div>
        <Button
          onClick={() => {
            if (needsIntake) {
              setIntakeValues(
                (intakeStatus?.clientData as Record<string, string>) || {},
              );
              setIntakeErrors({});
              setIntakeFiles({});
              setIntakePage(0);
              setShowIntakeForm(true);
            } else {
              const nextOrgId = !isAllProsMode
                ? null
                : organizations[0]?.orgId ?? null;
              const dur =
                isAllProsMode && nextOrgId
                  ? (organizations.find((x) => x.orgId === nextOrgId)
                    ?.defaultSessionDuration ?? 60)
                  : (organizations.find((x) => x.clientId === activeClientId)
                    ?.defaultSessionDuration ?? 60);
              setSelectedOrgIdForBooking(nextOrgId);
              setBookingWizardStep(1);
              setShowBooking(true);
              setBookingDate("");
              setBookingDuration(normalizeDurationMinutes(dur));
              setBookingType("");
              setSelectedServiceItem(null);
            }
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("booking.title")}
        </Button>
      </div>

      {needsIntake && (
        <InfoCallout variant="warning">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">
                Formulaire à compléter
              </p>
              <p className="text-xs text-amber-900 dark:text-amber-200">
                Veuillez remplir le formulaire d'informations avant de pouvoir
                réserver un rendez-vous.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500 bg-white/90 text-amber-900 hover:bg-amber-100 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
              onClick={() => {
                setIntakeValues(
                  (intakeStatus?.clientData as Record<string, string>) || {},
                );
                setIntakeErrors({});
                setIntakeFiles({});
                setIntakePage(0);
                setShowIntakeForm(true);
              }}
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              Remplir
            </Button>
          </div>
        </InfoCallout>
      )}

      <div className="flex gap-1 border-b">
        {(["upcoming", "past"] as const).map((tabKey) => (
          <Button
            key={tabKey}
            variant="ghost"
            onClick={() => setTab(tabKey)}
            className={cn(
              "rounded-none border-b-2 px-4 py-2",
              tab === tabKey
                ? "border-primary text-primary"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            {tabKey === "upcoming"
              ? t("appointments.tabUpcoming")
              : t("appointments.tabPast")}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="bg-muted h-20 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <Card className="flex h-64 items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            {tab === "upcoming" ? (
              <EmptyIllustration
                size={176}
                className="max-w-full text-muted-foreground"
              />
            ) : (
              <LottieIllustration
                animationData={emptyBoxLottie}
                size={200}
                className="opacity-95"
              />
            )}
            <p className="text-muted-foreground">
              {tab === "upcoming"
                ? t("appointments.noUpcoming")
                : t("appointments.noPast")}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((appt) => {
            const highlightId = searchParams.get("appointmentId");
            const modificationBlocked = isAppointmentModificationBlocked(
              appt,
              cancellationMinLead,
            );
            return (
              <Card
                key={appt.id}
                id={`appointment-card-${appt.id}`}
                className={cn(
                  "p-4 scroll-mt-24",
                  highlightId === appt.id && "ring-primary ring-2",
                )}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-lg",
                        statusColors[appt.status],
                        "text-white",
                      )}
                    >
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {new Date(appt.startsAt).toLocaleDateString(locale, {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                          })}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {statusLabels[appt.status]}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        <Clock className="h-3 w-3" />
                        {new Date(appt.startsAt).toLocaleTimeString(locale, {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" · "}
                        {appt.duration} min
                        {appt.type && ` · ${appt.type}`}
                      </div>
                      {appt.location?.name && (
                        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span>
                            {appt.location.name}
                            {appt.location.address
                              ? ` · ${appt.location.address}`
                              : ""}
                          </span>
                        </div>
                      )}
                      {appt.org?.name ? (
                        <div className="mt-1 text-xs font-medium opacity-80">
                          {appt.org.name}
                        </div>
                      ) : null}
                      {appt.status === "PENDING" &&
                        appt.pendingConfirmationBy === "CLIENT" && (
                          <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
                            {t("appointments.proposedByPractitioner")}
                          </p>
                        )}
                      {appt.confirmedAt && (
                        <p className="text-muted-foreground mt-1.5 text-xs">
                          {t("appointments.validatedOn", {
                            date: new Date(appt.confirmedAt).toLocaleString(
                              locale,
                              {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            ),
                          })}
                        </p>
                      )}
                      {appt.status === "CONFIRMED" &&
                        appt.confirmationMessage && (
                          <div className="border-primary/20 bg-primary/5 text-foreground mt-2 rounded-lg border px-3 py-2 text-sm">
                            <p className="text-primary/90 text-xs font-medium">
                              {t("appointments.messageFromPro")}
                            </p>
                            <p className="mt-0.5 whitespace-pre-wrap">
                              {appt.confirmationMessage}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {appt.depositPaid && appt.depositAmount > 0 && (
                      <Badge
                        variant="outline"
                        className="border-green-300 text-[10px] text-green-700 dark:text-green-400"
                      >
                        <ShieldCheck className="mr-1 h-3 w-3" />
                        {t("cancel.depositPaid", {
                          amount: formatEuroNumber(appt.depositAmount, locale, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }),
                        })}
                      </Badge>
                    )}
                    {appt.price > 0 && (
                      <span className="text-sm font-medium">
                        {formatMoneyEur(appt.price, locale, {
                          forceDecimals: true,
                        })}
                      </span>
                    )}

                    {tab === "upcoming" && appt.status !== "CANCELLED" && (
                      <div className="flex w-full flex-row flex-wrap justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="justify-center"
                          asChild
                        >
                          <a
                            href={generateGoogleCalendarUrl(appt)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center"
                          >
                            <ExternalLink className="hidden h-3 w-3 shrink-0 sm:mr-1 sm:inline-block" />
                            Google Cal
                          </a>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="justify-center"
                          disabled={chatCreatingApptId === appt.id}
                          onClick={async () => {
                            setChatCreatingApptId(appt.id);
                            try {
                              const subject = `${t(
                                "appointments.chatSubjectAppt",
                                "Question — rendez-vous",
                              )} · ${new Date(appt.startsAt).toLocaleDateString(locale)}`.slice(
                                0,
                                120,
                              );
                              const initialMessage = t(
                                "appointments.chatBodyAppt",
                                "Bonjour,\n\nJe vous contacte au sujet de mon rendez-vous du {{date}} à {{time}}.\n\n",
                                {
                                  date: new Date(appt.startsAt).toLocaleDateString(
                                    locale,
                                    {
                                      weekday: "long",
                                      day: "numeric",
                                      month: "long",
                                    },
                                  ),
                                  time: new Date(appt.startsAt).toLocaleTimeString(
                                    locale,
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  ),
                                },
                              );
                              const conv = await createPortalConversation(
                                subject,
                                appt.org?.id,
                                initialMessage,
                              );
                              navigate(`/chat/${conv.id}`);
                            } catch {
                              toast.error(
                                t(
                                  "appointments.chatError",
                                  "Impossible d’ouvrir la conversation.",
                                ),
                              );
                            } finally {
                              setChatCreatingApptId(null);
                            }
                          }}
                        >
                          {chatCreatingApptId === appt.id ? (
                            <Loader2 className="mr-1 h-3 w-3 shrink-0 animate-spin" />
                          ) : (
                            <MessageCircle className="hidden h-3 w-3 shrink-0 sm:mr-1 sm:inline-block" />
                          )}
                          {t("appointments.askAboutAppt", "Question")}
                        </Button>
                        {(appt.status === "PENDING" ||
                          appt.status === "CONFIRMED") && (
                            <>
                              {appt.status === "PENDING" &&
                                appt.pendingConfirmationBy === "CLIENT" && (
                                  <Button
                                    size="sm"
                                    className="justify-center"
                                    disabled={confirmMyMutation.isPending}
                                    onClick={() =>
                                      confirmMyMutation.mutate(appt.id, {
                                        onSuccess: () =>
                                          toast.success(
                                            t("appointments.confirmSuccess"),
                                          ),
                                        onError: () =>
                                          toast.error(
                                            t("appointments.confirmError"),
                                          ),
                                      })
                                    }
                                  >
                                    {confirmMyMutation.isPending ? (
                                      <Loader2 className="mr-1 h-3 w-3 shrink-0 animate-spin" />
                                    ) : (
                                      <Check className="mr-1 h-3 w-3 shrink-0" />
                                    )}
                                    {t("appointments.confirmAction")}
                                  </Button>
                                )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="justify-center"
                                disabled={modificationBlocked}
                                title={
                                  modificationBlocked
                                    ? t(
                                      "appointments.modificationTooLate",
                                      "Hors du délai d’annulation ou de modification défini par votre praticien.",
                                    )
                                    : undefined
                                }
                                onClick={() => {
                                  setRescheduleId(appt.id);
                                  setRescheduleDate("");
                                }}
                              >
                                <CalendarDays className="hidden h-3 w-3 shrink-0 sm:mr-1 sm:inline-block" />
                                {t("reschedule.title")}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="justify-center"
                                disabled={modificationBlocked}
                                title={
                                  modificationBlocked
                                    ? t(
                                      "appointments.modificationTooLate",
                                      "Hors du délai d’annulation ou de modification défini par votre praticien.",
                                    )
                                    : undefined
                                }
                                onClick={() => openCancelDialog(appt)}
                              >
                                <X className="hidden h-3 w-3 shrink-0 sm:mr-1 sm:inline-block" />
                                {t("common.cancel")}
                              </Button>
                            </>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(v) => {
          setCancelDialogOpen(v);
          if (!v) setPendingCancelAppt(null);
        }}
      >
        <DialogContent
          aria-describedby="client-cancel-appt-desc"
          className="sm:max-w-md sm:rounded-xl"
        >
          <DialogHeader>
            <DialogTitle>{t("cancel.dialogTitle")}</DialogTitle>
            <DialogDescription id="client-cancel-appt-desc">
              <span className="mb-3 block">{cancelDialogDescription}</span>
              {cancellationPolicyText?.trim() ? (
                <span className="border-input bg-muted/40 mt-3 block rounded-lg border px-3 py-2 text-xs">
                  <span className="text-foreground mb-1 block font-medium">
                    {t(
                      "appointments.cancellationPolicyTitle",
                      "Politique affichée aux clients",
                    )}
                  </span>
                  <span className="text-muted-foreground block whitespace-pre-wrap">
                    {cancellationPolicyText.trim()}
                  </span>
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCancelDialogOpen(false);
                setPendingCancelAppt(null);
              }}
            >
              {t("cancel.dialogDismiss")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelMutation.isPending}
              onClick={confirmCancelAppointment}
            >
              {cancelMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <X className="mr-2 h-4 w-4" />
              )}
              {t("cancel.confirmAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!rescheduleId}
        onOpenChange={(v) => !v && setRescheduleId(null)}
      >
        <DialogContent
          aria-describedby={undefined}
          className="left-1/2 top-[45%] max-w-[560px] gap-0 overflow-hidden p-0 sm:rounded-xl"
        >
          <div className="flex items-start justify-between border-b px-4 py-3 pr-10">
            <div>
              <DialogTitle className="text-base font-semibold">
                {t("reschedule.title")}
              </DialogTitle>
              {rescheduleAppt && (
                <p className="text-muted-foreground mt-1 text-[11px]">
                  {t("reschedule.current")} :{" "}
                  {new Date(rescheduleAppt.startsAt).toLocaleDateString(
                    locale,
                    {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    },
                  )}
                  {" · "}
                  {new Date(rescheduleAppt.startsAt).toLocaleTimeString(
                    locale,
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                  {" · "}
                  {rescheduleAppt.duration} min
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-[140px,1fr]">
            <div className="bg-muted/30 flex flex-col justify-center border-r px-4 py-5">
              <p className="text-muted-foreground text-center text-xs font-medium">
                {t("reschedule.pickDate")}
              </p>
              <p className="text-muted-foreground/90 mt-1 text-center text-[11px]">
                {t("booking.selectDayHint")}
              </p>
            </div>

            <div className="flex flex-col">
              <div className="border-b p-3">
                <MiniCalendar
                  activeDays={activeDays}
                  closedDates={closedDates ?? []}
                  selectedDate={rescheduleDate}
                  onSelect={setRescheduleDate}
                  onMonthChange={handleMonthChange}
                />
              </div>
              <div className="bg-muted/20 flex min-h-0 flex-1 flex-col">
                <p className="text-muted-foreground shrink-0 border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wider">
                  {rescheduleDate
                    ? new Date(rescheduleDate + "T12:00:00").toLocaleDateString(
                      locale,
                      {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      },
                    )
                    : t("reschedule.pickSlot")}
                </p>
                {rescheduleDate &&
                  availableSlots &&
                  availableSlots.length > 0 &&
                  !loadingSlots ? (
                  <p className="text-muted-foreground/90 shrink-0 border-b px-3 pb-2 text-[10px] leading-snug">
                    {t("booking.timesPracticeLocal")}
                  </p>
                ) : null}
                <div className="flex-1 overflow-y-auto">
                  {!rescheduleDate ? (
                    <p className="text-muted-foreground px-3 py-5 text-center text-[11px]">
                      {t("booking.selectDayHint")}
                    </p>
                  ) : loadingSlots ? (
                    <div className="flex justify-center py-5">
                      <Loader2 className="text-primary h-4 w-4 animate-spin" />
                    </div>
                  ) : !availableSlots || availableSlots.length === 0 ? (
                    <p className="text-muted-foreground px-3 py-5 text-center text-[11px]">
                      {t("reschedule.noSlots")}
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 gap-1 p-2">
                      {availableSlots.map((slot) => {
                        const isFull = !!slot.capacity?.isFull;
                        return (
                          <Button
                            key={slot.start}
                            type="button"
                            variant="ghost"
                            disabled={rescheduleMutation.isPending || isFull}
                            className={cn(
                              "hover:bg-primary hover:text-primary-foreground h-auto flex-col gap-0.5 rounded-md py-2 text-center text-xs font-medium shadow-none transition-colors disabled:opacity-50",
                              isFull && "text-amber-700 dark:text-amber-400",
                            )}
                            title={
                              isFull
                                ? t("booking.slotFullShort", "Complet")
                                : undefined
                            }
                            onClick={async () => {
                              if (!rescheduleId || isFull) return;
                              try {
                                await rescheduleMutation.mutateAsync({
                                  id: rescheduleId,
                                  startsAt: slot.start,
                                });
                                toast.success(t("reschedule.success"));
                                setRescheduleId(null);
                              } catch {
                                toast.error(t("reschedule.error"));
                              }
                            }}
                          >
                            {rescheduleMutation.isPending ? (
                              <Loader2 className="mx-auto h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <span>
                                  {new Date(slot.start).toLocaleTimeString(
                                    locale,
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )}
                                </span>
                                {slot.capacity && (
                                  <span className="text-[9px] font-normal tabular-nums opacity-80">
                                    {isFull
                                      ? t("booking.slotFullShort", "Complet")
                                      : `${slot.capacity.booked}/${slot.capacity.max}`}
                                  </span>
                                )}
                              </>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showIntakeForm} onOpenChange={setShowIntakeForm}>
        <DialogContent className="max-h-[85dvh] max-w-lg p-0 sm:rounded-2xl">
          <DialogHeader className="space-y-1.5 p-6 pb-2">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <ClipboardList className="text-primary h-5 w-5" />
              Formulaire d'informations
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Veuillez compléter ces informations pour pouvoir réserver un
              rendez-vous.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="px-6 pb-6">
            {totalIntakePages > 1 && (
              <div className="mb-4 flex items-center justify-center gap-1.5">
                {Array.from({ length: totalIntakePages }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      i === intakePage
                        ? "bg-primary w-6"
                        : "bg-muted-foreground/30 w-1.5",
                    )}
                  />
                ))}
              </div>
            )}

            <div className="space-y-5">
              <IntakeFormFields
                fields={visibleIntakeFields}
                values={intakeValues}
                fileInputs={intakeFiles}
                onFileChange={(id, file) => {
                  setIntakeFiles((prev) => ({ ...prev, [id]: file }));
                }}
                onChange={(id, val) => {
                  setIntakeValues((prev) => ({ ...prev, [id]: val }));
                  setIntakeErrors((prev) => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                  });
                }}
                errors={intakeErrors}
              />
              <div className="mt-2 flex justify-between gap-3 border-t pt-4">
                {intakePage > 0 ? (
                  <Button
                    variant="outline"
                    onClick={() => setIntakePage((p) => p - 1)}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Précédent
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowIntakeForm(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                )}

                {isLastIntakePage ? (
                  <Button
                    disabled={submitIntakePending}
                    onClick={async () => {
                      const errs: Record<string, string> = {};
                      for (const field of intakeFields) {
                        const hasValue = !!intakeValues[field.id]?.trim();
                        const hasDocumentFile =
                          field.type === "document" && !!intakeFiles[field.id];
                        if (field.required && !(hasValue || hasDocumentFile)) {
                          errs[field.id] = `${field.label} est requis`;
                        }
                      }
                      if (Object.keys(errs).length > 0) {
                        setIntakeErrors(errs);
                        const firstErrIdx = intakeFields.findIndex(
                          (f) => errs[f.id],
                        );
                        if (firstErrIdx >= 0) {
                          setIntakePage(
                            Math.floor(firstErrIdx / INTAKE_PAGE_SIZE),
                          );
                        }
                        return;
                      }
                      try {
                        if (hasDocumentIntakeFields) {
                          await submitIntakeWithDocumentsMutation.mutateAsync({
                            intakeData: intakeValues,
                            intakeFields,
                            files: intakeFiles,
                          });
                        } else {
                          await submitIntakeMutation.mutateAsync(intakeValues);
                        }
                        toast.success("Formulaire enregistré");
                        const nextOrgId = !isAllProsMode
                          ? null
                          : organizations[0]?.orgId ?? null;
                        const dur =
                          isAllProsMode && nextOrgId
                            ? (organizations.find((x) => x.orgId === nextOrgId)
                              ?.defaultSessionDuration ?? 60)
                            : (organizations.find(
                              (x) => x.clientId === activeClientId,
                            )?.defaultSessionDuration ?? 60);
                        setShowIntakeForm(false);
                        setSelectedOrgIdForBooking(nextOrgId);
                        setBookingWizardStep(1);
                        setShowBooking(true);
                        setBookingDate("");
                        setBookingDuration(normalizeDurationMinutes(dur));
                        setBookingType("");
                        setSelectedServiceItem(null);
                      } catch {
                        toast.error("Erreur lors de l'enregistrement");
                      }
                    }}
                  >
                    {submitIntakePending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Confirmer
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      const errs: Record<string, string> = {};
                      for (const field of visibleIntakeFields) {
                        const hasValue = !!intakeValues[field.id]?.trim();
                        const hasDocumentFile =
                          field.type === "document" && !!intakeFiles[field.id];
                        if (field.required && !(hasValue || hasDocumentFile)) {
                          errs[field.id] = `${field.label} est requis`;
                        }
                      }
                      if (Object.keys(errs).length > 0) {
                        setIntakeErrors(errs);
                        return;
                      }
                      setIntakePage((p) => p + 1);
                    }}
                  >
                    Suivant
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showBooking}
        onOpenChange={(open) => {
          setShowBooking(open);
          if (!open) setBookingWizardStep(1);
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          className={cn(
            "flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 transition-[max-width] duration-200 sm:rounded-xl",
            bookingWizardStep === 1
              ? "max-w-[min(100vw-1.5rem,28rem)]"
              : "max-w-[min(100vw-1.5rem,48rem)]",
            bookingUsesServiceCatalog &&
            "lg:max-w-[min(100vw-2rem,68rem)] lg:max-h-[min(92dvh,840px)]",
          )}
        >
          <div className="border-border/80 bg-background shrink-0 border-b px-5 pb-4 pt-5 pr-12">
            <div className="flex items-start gap-1">
              {bookingWizardStep > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-foreground -ml-2.5 h-11 w-11 shrink-0 rounded-full"
                  aria-label={
                    bookingWizardStep === 3
                      ? t("booking.backToCalendar")
                      : t("booking.backToService")
                  }
                  onClick={handleBookingWizardBack}
                >
                  <ChevronLeft className="h-6 w-6" aria-hidden />
                </Button>
              ) : null}
              <div className="min-w-0 flex-1 pt-0.5">
                <DialogTitle className="text-lg font-semibold tracking-tight">
                  {t("booking.title")}
                </DialogTitle>
                <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                  {t("booking.selectDayHint")}
                </p>
              </div>
            </div>
          </div>

          <nav
            className={cn(
              "text-muted-foreground flex shrink-0 flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b px-5 py-2.5 text-xs sm:justify-start",
              bookingUsesServiceCatalog && "lg:hidden",
            )}
            aria-label={t("booking.title")}
          >
            {bookingUsesServiceCatalog ? (
              <>
                {bookingWizardStep > 1 ? (
                  <button
                    type="button"
                    onClick={() => setBookingWizardStep(1)}
                    className="bg-muted text-foreground hover:bg-muted/80 focus-visible:ring-ring cursor-pointer rounded-full px-2 py-0.5 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    1 · {t("booking.wizardStepService")}
                  </button>
                ) : (
                  <span
                    className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-medium"
                    aria-current="step"
                  >
                    1 · {t("booking.wizardStepService")}
                  </span>
                )}
                <span aria-hidden className="opacity-40">
                  —
                </span>
                {bookingWizardStep === 3 ? (
                  <button
                    type="button"
                    onClick={() => setBookingWizardStep(2)}
                    className="bg-muted text-foreground hover:bg-muted/80 focus-visible:ring-ring cursor-pointer rounded-full px-2 py-0.5 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    2 · {t("booking.wizardStepDate")}
                  </button>
                ) : (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 font-medium transition-colors",
                      bookingWizardStep === 2
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted",
                    )}
                    aria-current={
                      bookingWizardStep === 2 ? "step" : undefined
                    }
                  >
                    2 · {t("booking.wizardStepDate")}
                  </span>
                )}
                <span aria-hidden className="opacity-40">
                  —
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 font-medium transition-colors",
                    bookingWizardStep === 3
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                  aria-current={bookingWizardStep === 3 ? "step" : undefined}
                >
                  3 · {t("booking.wizardStepSlot")}
                </span>
              </>
            ) : (
              <>
                {bookingWizardStep === 2 ? (
                  <button
                    type="button"
                    onClick={() => setBookingWizardStep(1)}
                    className="bg-muted text-foreground hover:bg-muted/80 focus-visible:ring-ring cursor-pointer rounded-full px-2 py-0.5 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
                  >
                    1 · {t("booking.wizardStepService")}
                  </button>
                ) : (
                  <span
                    className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-medium"
                    aria-current="step"
                  >
                    1 · {t("booking.wizardStepService")}
                  </span>
                )}
                <span aria-hidden className="opacity-40">
                  —
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 font-medium transition-colors",
                    bookingWizardStep === 2
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                >
                  2 · {t("booking.wizardStepSlot")}
                </span>
              </>
            )}
          </nav>

          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain",
              bookingUsesServiceCatalog &&
              "lg:grid lg:min-h-0 lg:grid-cols-2 lg:grid-rows-1 lg:divide-border lg:divide-x lg:overflow-y-hidden",
            )}
          >
            {/* Étape 1 : Service / Durée + Type */}
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col bg-muted/30",
                bookingWizardStep !== 1 &&
                (bookingUsesServiceCatalog ? "hidden lg:flex" : "hidden"),
                bookingUsesServiceCatalog && "lg:flex-none lg:min-h-0",
              )}
            >
              <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5 sm:p-6 lg:min-h-0">
                {isAllProsMode && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Praticien</Label>
                    <div className="flex flex-wrap gap-2">
                      {organizations.map((org) => (
                        <Button
                          key={org.orgId}
                          size="sm"
                          variant={
                            selectedOrgIdForBooking === org.orgId
                              ? "default"
                              : "outline"
                          }
                          onClick={() => {
                            setSelectedOrgIdForBooking(org.orgId);
                            setSelectedServiceItem(null);
                            setServiceSearch("");
                            setBookingDate("");
                            setBookingWizardStep(1);
                          }}
                        >
                          {org.orgName}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                {serviceCatalogPending ? (
                  <div className="flex min-h-32 flex-1 items-center justify-center">
                    <Loader2 className="text-primary h-6 w-6 animate-spin" />
                  </div>
                ) : hasServiceItems ? (
                  <div className="flex flex-col gap-4">
                    <Label className="text-sm font-medium">
                      <ShoppingBag className="mr-1.5 inline h-4 w-4" />
                      Prestation
                    </Label>

                    {currentServiceItems.length > 3 && (
                      <div className="relative">
                        <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                        <Input
                          placeholder={t("booking.searchServicePlaceholder")}
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                          className="bg-background h-10 rounded-lg pl-10 text-sm shadow-sm"
                        />
                      </div>
                    )}

                    {(() => {
                      const needle = serviceSearch.toLowerCase().trim();
                      const filtered = needle
                        ? currentServiceItems.filter((item) => {
                            if (
                              item.label.toLowerCase().includes(needle)
                            ) {
                              return true;
                            }
                            const desc = richTextToPlain(item.description)
                              .toLowerCase()
                              .replace(/\s+/g, " ");
                            return desc.includes(needle);
                          })
                        : currentServiceItems;

                      if (filtered.length === 0) {
                        return (
                          <p className="text-muted-foreground py-4 text-center text-sm">
                            {t("booking.noServiceMatch")}
                          </p>
                        );
                      }

                      return (
                        <SessionTypeServiceSelect
                          value={selectedServiceItem?.id ?? null}
                          onValueChange={(id) => {
                            if (!id) {
                              setSelectedServiceItem(null);
                              setSelectedLocationId(null);
                              setBookingDate("");
                              setBookingWizardStep(1);
                              return;
                            }
                            const item = filtered.find((i) => i.id === id);
                            if (!item) return;
                            if (
                              isSessionPackExhausted(
                                item.maxBookingsPerClient,
                                item.remainingBookings,
                              )
                            ) {
                              return;
                            }
                            setSelectedServiceItem(item);
                            setSelectedLocationId(null);
                            setBookingDate("");
                            setBookingWizardStep((step) =>
                              step > 1 ? 2 : step,
                            );
                          }}
                          ariaLabel={t("booking.serviceSelectAria")}
                          unselectedOptionLabel={t(
                            "booking.selectServicePlaceholder",
                          )}
                          selectPlaceholder={t(
                            "booking.selectServicePlaceholder",
                          )}
                          items={filtered.map((item) => {
                            const packExhausted = isSessionPackExhausted(
                              item.maxBookingsPerClient,
                              item.remainingBookings,
                            );
                            const descPlain = richTextToPlain(item.description)
                              .replace(/\s+/g, " ")
                              .trim();
                            const imageUrl = item.image
                              ? (resolveOrganizationMediaUrl(item.image) ??
                                item.image)
                              : null;
                            const max = item.maxBookingsPerClient;
                            const rem = item.remainingBookings;
                            let remainingSlot: ReactNode | undefined;
                            if (shouldShowSessionPackRemaining(max, rem)) {
                              if (rem <= 0) {
                                remainingSlot = (
                                  <span className="text-destructive text-[10px] leading-none">
                                    {t("booking.badgePackExhausted")}
                                  </span>
                                );
                              } else if (rem === 1) {
                                remainingSlot = (
                                  <span className="text-primary text-[10px] leading-none">
                                    {t("booking.badgeRemainingOne")}
                                  </span>
                                );
                              } else {
                                remainingSlot = (
                                  <span className="text-muted-foreground text-[10px] leading-none">
                                    {t("booking.badgeRemainingMany", {
                                      count: rem,
                                    })}
                                  </span>
                                );
                              }
                            }

                            return {
                              id: item.id,
                              label: item.label,
                              descriptionPlain:
                                descPlain.length > 0 ? descPlain : null,
                              durationMin: item.duration,
                              priceLabel: formatMoneyEur(item.price, locale, {
                                forceDecimals: true,
                              }),
                              imageUrl,
                              imageObjectPosition: item.imageObjectPosition,
                              disabled: packExhausted,
                              dimmed: packExhausted,
                              remainingSlot,
                            };
                          })}
                        />
                      );
                    })()}

                    {/* Location selector */}
                    {(() => {
                      const serviceLocations =
                        selectedServiceItem?.locations ?? [];
                      if (serviceLocations.length === 0) return null;
                      if (serviceLocations.length === 1) {
                        const first = serviceLocations[0];
                        if (!first) return null;
                        const loc = first.location;
                        return (
                          <div className="bg-muted/40 flex items-center gap-2 rounded-lg border p-3">
                            <MapPin className="text-primary h-4 w-4 shrink-0" />
                            <div>
                              <p className="text-sm font-medium">{loc.name}</p>
                              {loc.address && (
                                <p className="text-muted-foreground text-xs">
                                  {loc.address}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Lieu de la séance
                          </Label>
                          <div className="space-y-1.5">
                            {serviceLocations.map(({ location: loc }) => (
                              <Button
                                key={loc.id}
                                type="button"
                                variant="ghost"
                                onClick={() => setSelectedLocationId(loc.id)}
                                className={cn(
                                  "flex h-auto w-full items-center justify-start gap-3 rounded-lg border p-3 text-left text-sm font-normal shadow-none transition-all",
                                  selectedLocationId === loc.id
                                    ? "border-primary bg-primary/5 ring-primary ring-1"
                                    : "hover:bg-muted/45",
                                )}
                              >
                                <MapPin className="text-primary h-4 w-4 shrink-0" />
                                <div>
                                  <p className="font-medium">{loc.name}</p>
                                  {loc.address && (
                                    <p className="text-muted-foreground text-xs">
                                      {loc.address}
                                    </p>
                                  )}
                                </div>
                              </Button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        {t("booking.duration")}
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {durationOptions.map((d) => (
                          <Button
                            key={d}
                            variant={
                              bookingDuration === d ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => {
                              setBookingDuration(d);
                              setBookingDate("");
                            }}
                          >
                            {d} min
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        {t("booking.typeOptional")}
                      </Label>
                      <Input
                        className="bg-background"
                        value={bookingType}
                        onChange={(e) => setBookingType(e.target.value)}
                        placeholder={t("booking.typePlaceholder")}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Étape 2 : date · Étape 3 (mobile catalogue) : créneaux */}
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col overflow-hidden bg-background",
                bookingWizardStep !== 2 &&
                bookingWizardStep !== 3 &&
                (bookingUsesServiceCatalog ? "hidden lg:flex" : "hidden"),
                bookingUsesServiceCatalog && "lg:flex-none lg:min-h-0",
              )}
            >
              {(bookingWizardStep >= 2 || bookingUsesServiceCatalog) && (
                <div className="border-border/60 shrink-0 border-b px-5 pb-3 pt-5 sm:px-6 lg:px-5">
                  <div className="mb-0">
                    {selectedServiceItem ? (
                      <BookingWizardSelectedServiceRecap
                        service={selectedServiceItem}
                        locale={locale}
                        stepLabel={t("booking.wizardStepService")}
                      />
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        {effectiveBookingDuration} min · {t("booking.duration")}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div
                className={cn(
                  "border-border/60 shrink-0 border-b px-5 pb-4 pt-4 sm:px-6 lg:px-5",
                  bookingUsesServiceCatalog &&
                  bookingWizardStep === 3 &&
                  "hidden lg:block",
                  bookingNeedsServicePick && "pointer-events-none opacity-50",
                )}
              >
                <p className="text-foreground mb-3 text-sm font-semibold">
                  {t("booking.selectDate")}
                </p>
                <MiniCalendar
                  activeDays={bookingActiveDays}
                  closedDates={bookingClosedDates}
                  selectedDate={bookingDate}
                  onSelect={handleSelectBookingDate}
                  onMonthChange={handleMonthChange}
                />
              </div>
              <div
                className={cn(
                  "flex min-h-0 flex-1 flex-col overflow-hidden px-5 pb-5 pt-4 sm:px-6 sm:pb-6 lg:min-h-0 lg:px-5 lg:pb-5",
                  bookingUsesServiceCatalog &&
                  bookingWizardStep === 2 &&
                  "max-lg:hidden",
                  bookingNeedsServicePick && "pointer-events-none opacity-50",
                )}
              >
                <p className="text-foreground mb-2 text-sm font-semibold">
                  {bookingDate
                    ? t("booking.slotsFor", {
                      date: new Date(
                        bookingDate + "T12:00:00",
                      ).toLocaleDateString(locale, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      }),
                    })
                    : t("booking.available")}
                </p>
                {bookingDate &&
                  effectiveBookingSlots &&
                  effectiveBookingSlots.length > 0 &&
                  !effectiveLoadingBookingSlots ? (
                  <p className="text-muted-foreground mb-3 text-xs leading-relaxed">
                    {t("booking.timesPracticeLocal")}
                  </p>
                ) : null}
                {!bookingDate ? (
                  <div className="border-border/60 bg-muted/25 flex min-h-36 flex-1 items-center justify-center rounded-xl border border-dashed px-4">
                    <p className="text-muted-foreground text-center text-sm leading-relaxed">
                      {serviceCatalogPending
                        ? t("booking.loadingServices")
                        : bookingNeedsServicePick
                          ? t("booking.pickServiceFirst")
                          : t("booking.selectDayHint")}
                    </p>
                  </div>
                ) : effectiveLoadingBookingSlots ? (
                  <div className="flex min-h-36 flex-1 items-center justify-center">
                    <Loader2 className="text-primary h-6 w-6 animate-spin" />
                  </div>
                ) : !effectiveBookingSlots || effectiveBookingSlots.length === 0 ? (
                  <div className="border-border/60 bg-muted/25 flex min-h-36 flex-1 items-center justify-center rounded-xl border border-dashed px-4">
                    <p className="text-muted-foreground text-center text-sm leading-relaxed">
                      {t("booking.noSlots")}
                    </p>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "grid min-h-0 flex-1 grid-cols-2 gap-2.5 overflow-y-auto max-h-[min(56vh,28rem)] sm:grid-cols-3 sm:max-h-[min(60vh,32rem)]",
                      bookingUsesServiceCatalog &&
                      "lg:max-h-[min(62vh,480px)]",
                    )}
                  >
                    {effectiveBookingSlots.map((slot) => {
                      const capacity = slot.capacity;
                      const isFull = !!capacity?.isFull;
                      const isJoiningThisSlot =
                        joinWaitlistMutation.isPending &&
                        joinWaitlistMutation.variables?.startsAt === slot.start;
                      const isBookingPending = isAllProsMode
                        ? aggregateBookMutation.isPending
                        : bookMutation.isPending;

                      return (
                        <Button
                          key={slot.start}
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            bookingNeedsServicePick ||
                            isBookingPending ||
                            joinWaitlistMutation.isPending ||
                            (isAllProsMode && !effectiveOrgId) ||
                            (isFull && isAllProsMode)
                          }
                          className={cn(
                            "h-auto min-h-10 flex-col gap-0.5 justify-center rounded-lg py-1.5 text-sm font-medium shadow-none transition-colors lg:min-h-11 lg:text-base",
                            isFull &&
                            "border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400",
                          )}
                          onClick={async () => {
                            if (bookingNeedsServicePick) return;
                            if (hasServiceItems && !selectedServiceItem) {
                              toast.error(t("booking.pickServiceFirst"));
                              return;
                            }

                            if (isFull) {
                              if (!selectedServiceItem || isAllProsMode) return;
                              try {
                                await joinWaitlistMutation.mutateAsync({
                                  serviceItemId: selectedServiceItem.id,
                                  startsAt: slot.start,
                                });
                                toast.success(
                                  t(
                                    "booking.waitlistJoined",
                                    "Vous êtes sur la liste d'attente",
                                  ),
                                );
                                setShowBooking(false);
                              } catch (err: unknown) {
                                const msg = (
                                  err as {
                                    response?: { data?: { message?: string } };
                                  }
                                )?.response?.data?.message;
                                toast.error(msg || t("booking.error"));
                              }
                              return;
                            }

                            try {
                              if (isAllProsMode) {
                                if (!effectiveOrgId) {
                                  toast.error("Sélectionne d'abord un praticien");
                                  return;
                                }
                                await aggregateBookMutation.mutateAsync({
                                  orgId: effectiveOrgId,
                                  startsAt: slot.start,
                                  duration: effectiveBookingDuration,
                                  type: bookingType || undefined,
                                  ...(selectedServiceItem && {
                                    serviceItemId: selectedServiceItem.id,
                                  }),
                                  ...(selectedLocationId && {
                                    locationId: selectedLocationId,
                                  }),
                                });
                              } else {
                                await bookMutation.mutateAsync({
                                  startsAt: slot.start,
                                  duration: effectiveBookingDuration,
                                  type: bookingType || undefined,
                                  ...(selectedServiceItem && {
                                    serviceItemId: selectedServiceItem.id,
                                  }),
                                  ...(selectedLocationId && {
                                    locationId: selectedLocationId,
                                  }),
                                });
                              }
                              toast.success(t("booking.success"));
                              setShowBooking(false);
                            } catch (err: unknown) {
                              const msg = (
                                err as {
                                  response?: { data?: { message?: string } };
                                }
                              )?.response?.data?.message;
                              if (msg?.includes("formulaire")) {
                                setShowBooking(false);
                                setIntakeValues(
                                  (intakeStatus?.clientData as Record<
                                    string,
                                    string
                                  >) || {},
                                );
                                setIntakeErrors({});
                                setIntakeFiles({});
                                setIntakePage(0);
                                setShowIntakeForm(true);
                                toast.error(
                                  "Veuillez d'abord remplir le formulaire d'informations",
                                );
                              } else {
                                toast.error(msg || t("booking.error"));
                              }
                            }
                          }}
                        >
                          {isBookingPending || isJoiningThisSlot ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <span>
                                {new Date(slot.start).toLocaleTimeString(
                                  locale,
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </span>
                              {capacity && (
                                <span className="text-[10px] font-normal tabular-nums opacity-80">
                                  {isFull ? (
                                    isAllProsMode ? (
                                      t("booking.slotFullShort", "Complet")
                                    ) : (
                                      <span className="inline-flex items-center gap-0.5">
                                        <ListPlus className="h-2.5 w-2.5" />
                                        {t(
                                          "booking.waitlistJoinShort",
                                          "Liste d'attente",
                                        )}
                                      </span>
                                    )
                                  ) : (
                                    `${capacity.booked}/${capacity.max}`
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-muted/15 flex shrink-0 flex-col gap-2 border-t px-5 py-3">
            {bookingWizardStep === 1 ? (
              <>
                <div
                  className={cn(
                    "flex gap-2",
                    bookingUsesServiceCatalog && "lg:justify-end",
                  )}
                >
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "min-h-11 flex-1",
                      bookingUsesServiceCatalog && "lg:min-w-36 lg:flex-none",
                    )}
                    onClick={() => setShowBooking(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="button"
                    className={cn(
                      "min-h-11 flex-1",
                      bookingUsesServiceCatalog && "lg:hidden",
                    )}
                    disabled={
                      bookingNeedsLocationPick ||
                      bookingNeedsServicePick ||
                      (isAllProsMode && !effectiveOrgId)
                    }
                    onClick={() => {
                      if (bookingNeedsServicePick) return;
                      setBookingWizardStep(2);
                    }}
                  >
                    {t("booking.continueToSlots")}
                  </Button>
                </div>
                {bookingNeedsLocationPick ? (
                  <p className="text-destructive text-center text-xs">
                    {t("booking.pickLocationFirst")}
                  </p>
                ) : null}
                {serviceCatalogPending ? (
                  <p className="text-muted-foreground text-center text-xs">
                    {t("booking.loadingServices")}
                  </p>
                ) : null}
                {bookingNeedsServicePick && !serviceCatalogPending ? (
                  <p className="text-destructive text-center text-xs">
                    {t("booking.pickServiceFirst")}
                  </p>
                ) : null}
                {isAllProsMode && !effectiveOrgId ? (
                  <p className="text-muted-foreground text-center text-xs">
                    {t("booking.pickPractitionerFirst")}
                  </p>
                ) : null}
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "min-h-11 w-full gap-2",
                  bookingUsesServiceCatalog && "lg:hidden",
                )}
                onClick={handleBookingWizardBack}
              >
                <ChevronLeft className="h-5 w-5 shrink-0" aria-hidden />
                <span>
                  {bookingWizardStep === 3
                    ? t("booking.backToCalendar")
                    : t("booking.backToService")}
                </span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
